import React, { useState } from 'react';
import { Alert, Box, Button, CircularProgress, TextField } from '@mui/material';
import { MarkEmailUnreadOutlined as EmailIcon } from '@mui/icons-material';
import { apiClient } from '../../services/api';
import { ApiError } from '../../services/api/http';
import { useLanguage } from '../../contexts/LanguageContext';
import AuthPageShell from './AuthPageShell';

const ForgotPassword: React.FC = () => {
  const { t } = useLanguage();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    setSuccess('');
    if (!email.trim()) {
      setError(t('auth.enterEmailAddress'));
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setError(t('auth.emailInvalid'));
      return;
    }
    setIsLoading(true);
    try {
      await apiClient.forgotPassword(email.trim());
      // Keep this public response localized and intentionally generic.
      setSuccess(t('auth.passwordResetRequestAccepted'));
    } catch (err: unknown) {
      setError(err instanceof ApiError ? err.message : t('auth.passwordResetRequestFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthPageShell title={t('auth.forgotPassword')} description={t('auth.forgotPasswordDescription')}
      icon={<EmailIcon />} backTo="/login" backLabel={t('auth.backToLogin')}>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
      <Box component="form" onSubmit={submit} noValidate sx={{ display: 'grid', gap: 2 }}>
        <TextField fullWidth autoFocus type="email" label={t('auth.emailAddress')}
          autoComplete="email" value={email} onChange={event => { setEmail(event.target.value); setError(''); }}
          disabled={isLoading || !!success} error={!!error} required />
        <Button type="submit" fullWidth variant="contained" disabled={isLoading || !!success}>
          {isLoading ? <CircularProgress size={24} /> : t('auth.sendPasswordResetLink')}
        </Button>
      </Box>
    </AuthPageShell>
  );
};

export default ForgotPassword;
